/**
 * A kitten's meow, synthesised on the spot: a sawtooth that glides up and
 * back down through a sweeping bandpass (the "mi-au"), a triangle an octave
 * above for the kitten brightness, a touch of vibrato. No audio file, no
 * licence, ~600 ms.
 *
 * Browsers only let audio start after a touch. We try at once; if the
 * context stays suspended, the very first tap anywhere plays it.
 */
let ctx: AudioContext | null = null

function context(): AudioContext | null {
  if (ctx) return ctx
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
    return ctx
  } catch {
    return null
  }
}

function synth(ac: AudioContext) {
  const t0 = ac.currentTime + 0.02
  const dur = 0.62

  const master = ac.createGain()
  master.gain.setValueAtTime(0.0001, t0)
  master.gain.exponentialRampToValueAtTime(0.45, t0 + 0.06)
  master.gain.setValueAtTime(0.45, t0 + 0.34)
  master.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  master.connect(ac.destination)

  const formant = ac.createBiquadFilter()
  formant.type = 'bandpass'
  formant.Q.value = 2.2
  formant.frequency.setValueAtTime(900, t0)
  formant.frequency.linearRampToValueAtTime(2300, t0 + 0.2)
  formant.frequency.linearRampToValueAtTime(1100, t0 + dur)
  formant.connect(master)

  const voice = ac.createOscillator()
  voice.type = 'sawtooth'
  voice.frequency.setValueAtTime(560, t0)
  voice.frequency.linearRampToValueAtTime(860, t0 + 0.2)
  voice.frequency.linearRampToValueAtTime(480, t0 + dur)
  voice.connect(formant)

  const vibrato = ac.createOscillator()
  vibrato.frequency.value = 6.5
  const depth = ac.createGain()
  depth.gain.value = 14
  vibrato.connect(depth)
  depth.connect(voice.frequency)

  const shine = ac.createOscillator()
  shine.type = 'triangle'
  shine.frequency.setValueAtTime(1120, t0)
  shine.frequency.linearRampToValueAtTime(1720, t0 + 0.2)
  shine.frequency.linearRampToValueAtTime(960, t0 + dur)
  const shineGain = ac.createGain()
  shineGain.gain.value = 0.22
  shine.connect(shineGain)
  shineGain.connect(formant)

  for (const node of [voice, vibrato, shine]) {
    node.start(t0)
    node.stop(t0 + dur + 0.05)
  }
}

function onFirstGesture(fn: () => void) {
  const once = () => {
    document.removeEventListener('pointerdown', once, true)
    document.removeEventListener('keydown', once, true)
    fn()
  }
  document.addEventListener('pointerdown', once, true)
  document.addEventListener('keydown', once, true)
}

/** Plays the meow now if the browser allows it, or on the next tap. */
export function meow(): void {
  const ac = context()
  if (!ac) return
  const play = () => {
    void ac
      .resume()
      .then(() => {
        if (ac.state === 'running') synth(ac)
      })
      .catch(() => {})
  }
  if (ac.state === 'running') {
    synth(ac)
    return
  }
  void ac
    .resume()
    .then(() => {
      if (ac.state === 'running') synth(ac)
      else onFirstGesture(play)
    })
    .catch(() => onFirstGesture(play))
}
